import React, { useState, useEffect, useCallback, useOptimistic } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useNotification } from '@dynamon-as/ui';
import { Request, UpdateRequestDto, RequestStatus } from '@features/requests';
import { useRequests, useRequestStore } from '@features/requests';
import { useRequestComments } from '@features/requests/hooks';
import { useEmployeeId } from '@shared/hooks';
import {
    RequestForm,
    RequestChatLayout,
    RequestChat,
} from '@features/requests/components';
import { toEmployeeMessages } from '@features/requests/utils';

import { NOTIFICATION_MESSAGES } from '@features/requests/constants';
import styles from './RequestEdit.module.css';

/**
 * Request Edit Page
 * Page for editing an existing request
 */
export default function RequestEdit(): React.ReactElement {
    // Get request ID from URL params
    const { id } = useParams<{ id: string }>();

    // Router navigation
    const navigate = useNavigate();

    // Load orgEmployee and get orgEmployeeId using useEmployeeId hook
    // This triggers the org-employee fetch so orgEmployeeId is available on direct navigation
    const { orgEmployeeId, isLoading: isOrgEmployeeLoading } = useEmployeeId();

    // Use the requests hook with the real orgEmployeeId and requestId
    // This allows direct loading of the specific request for editing
    const {
        requests,
        isLoading: isRequestsLoading,
        updateRequest: updateRequestStore,
        error,
    } = useRequests(orgEmployeeId, id);

    // Get store for direct access to clearError
    const store = useRequestStore();

    // Check if we're still loading orgEmployee or the request
    const isLoading =
        isOrgEmployeeLoading ?? isRequestsLoading ?? !orgEmployeeId;

    // Use Notification from @dynamon-as/ui
    const { notify } = useNotification();

    // State for the request being edited
    const [editingRequest, setEditingRequest] = useState<Request | null>(null);

    // Load request once orgEmployee and the request have been loaded
    useEffect((): void => {
        if (isLoading) {
            return;
        }

        if (id) {
            const request = requests.find((r): boolean => r.id === id);
            if (request) {
                // Check if request can be edited (DRAFT or DECLINED)
                if (
                    request.status !== RequestStatus.DRAFT &&
                    request.status !== RequestStatus.REJECTED
                ) {
                    notify.error(
                        'Kun søknader i Utkast- eller Avslått-status kan redigeres.',
                    );
                    navigate('/requests');
                    return;
                }

                // Check if deadline has passed (only for DRAFT requests)
                if (
                    request.status === RequestStatus.DRAFT &&
                    request.deadline
                ) {
                    const deadlineDate = new Date(request.deadline);
                    deadlineDate.setHours(0, 0, 0, 0);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    if (deadlineDate < today) {
                        notify.error(
                            'Fristen for denne søknaden har utgått. Søknaden kan ikke redigeres.',
                        );
                        navigate('/requests');
                        return;
                    }
                }
                setEditingRequest(request);
            } else {
                notify.error('Søknad ikke funnet');
                navigate('/requests');
            }
        } else {
            navigate('/requests');
        }
    }, [id, requests, navigate, notify, isLoading]);

    // Request comments hook
    const { addComment: addCommentApi } = useRequestComments(id);

    // Map request transactions to messages
    const baseMessages = toEmployeeMessages(editingRequest?.transactions);

    // Use optimistic updates for comments
    const [optimisticMessages, addOptimisticMessage] = useOptimistic(
        baseMessages,
        (
            currentMessages: typeof baseMessages,
            newMessage: string,
        ): typeof baseMessages => {
            // Add the new message optimistically
            return [
                ...currentMessages,
                {
                    id: `optimistic-${Date.now()}`,
                    message: newMessage,
                    registered: new Date(),
                    status: 'COMMENTED',
                    priority: 0,
                },
            ];
        },
    );

    // Handle adding a comment with optimistic updates
    const handleAddComment = useCallback(
        async (text: string): Promise<boolean> => {
            const trimmed = text.trim();
            if (!trimmed) {
                return false;
            }

            // Add optimistic message
            addOptimisticMessage(trimmed);

            try {
                const ok = await addCommentApi(trimmed);
                if (ok) {
                    notify.success('Kommentar lagt til');
                } else {
                    notify.error('Kunne ikke sende kommentar');
                }
                return ok;
            } catch {
                notify.error('Kunne ikke sende kommentar');
                return false;
            }
        },
        [addCommentApi, notify, addOptimisticMessage],
    );

    // Show error notifications
    useEffect((): void => {
        if (error) {
            notify.error(error);
            store.clearError();
        }
    }, [error, notify, store]);

    /**
     * Handles form submission for updating a request
     */
    const handleUpdateSubmit = useCallback(
        async (dto: UpdateRequestDto): Promise<void> => {
            if (!id || !editingRequest) return;

            // Validation handled by store and type system

            try {
                // Use the DTO directly from the form, but clear notes
                // since they'll be sent as comment after successful save
                const updateDto: UpdateRequestDto = {
                    ...dto,
                    notes: '',
                };

                // First, save the request
                const updatedRequest = await updateRequestStore(id, updateDto);

                if (!updatedRequest) {
                    notify.error('Feil ved oppdatering av søknad');
                    return;
                }

                // Only send comment if request was saved successfully and there are notes
                if (dto.notes && dto.notes.trim()) {
                    const notesText: string = dto.notes;
                    const commentAdded = await addCommentApi(notesText);
                    if (commentAdded) {
                        notify.success(
                            'Søknad oppdatert og kommentar lagt til',
                        );
                    } else {
                        notify.success(NOTIFICATION_MESSAGES.REQUEST_UPDATED);
                    }
                } else {
                    notify.success(NOTIFICATION_MESSAGES.REQUEST_UPDATED);
                }

                // Navigate to request details after successful save
                navigate(`/requests/${id}`);
            } catch {
                notify.error('Feil ved oppdatering av søknad');
            }
        },
        [
            id,
            editingRequest,
            updateRequestStore,
            notify,
            navigate,
            addCommentApi,
        ],
    );

    /**
     * Handles form cancel - go back to details
     */
    const handleFormCancel = (): void => {
        if (id) {
            navigate(`/requests/${id}`);
        } else {
            navigate('/requests');
        }
    };

    if (!editingRequest) {
        return (
            <div className={styles.container}>
                <p>Laster søknad...</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <RequestChatLayout
                chat={
                    <RequestChat
                        messages={optimisticMessages}
                        onSend={handleAddComment}
                        isSending={false}
                    />
                }
                chatBadge={optimisticMessages.length}
            >
                <RequestForm
                    initialValues={editingRequest}
                    onSubmit={handleUpdateSubmit}
                    onCancel={handleFormCancel}
                    isCreate={false}
                    initialExcludedDates={editingRequest.excludedDates}
                />
            </RequestChatLayout>
        </div>
    );
}
